import type { FC, ReactNode } from 'react'

import ExpandView from '@/components/generals/expand-view'
import TvScene from '@/components/landing/mockups/tv-scene'
import SectionIntro from '@/components/landing/section-intro'

const STEPS = [
    'Open the TV app.',
    'Connect both devices to the same network.',
    'Scan the QR code from your phone.'
]

const GoogleTvSection: FC = (): ReactNode => {
    return (
        <section id='google-tv' aria-labelledby='google-tv-title' className='border-t border-border bg-background py-20 lg:py-28'>
            <div className='mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16'>
                <div>
                    <SectionIntro
                        id='google-tv-title'
                        eyebrow='Android + Google TV'
                        title={(
                            <>
                                From your phone
                                <br />
                                to the big screen.
                            </>
                        )}
                        intro="Connect the compatible apps on the same local network. Scan the TV's QR code and send your videos and photos from your phone."
                    />

                    <ol className='mt-9 space-y-4'>
                        {STEPS.map((step, index) => (
                            <li key={step} className='flex items-baseline gap-4 text-[0.97rem] text-foreground'>
                                <span className='font-mono text-xs font-semibold text-brand'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>

                                {step}
                            </li>
                        ))}
                    </ol>

                    <p className='mt-9 max-w-xl text-[0.82rem] leading-relaxed text-muted-foreground'>
                        Requires the compatible apps and access to the feature according to your plan. The TV supports up to four views; photos use their own viewer. YouTube opens in an official full-screen viewer with independent controls.
                    </p>
                </div>

                <figure className='reveal'>
                    <div className='relative'>
                        <div role='img' aria-label='Illustration of a living room TV showing four videos sent from a phone on the same network'>
                            <TvScene />
                        </div>

                        <ExpandView
                            title='Videos on the big screen'
                            caption='Illustrative view.'
                            ratio={1.45}
                            className='absolute right-3 top-3'
                        >
                            <TvScene />
                        </ExpandView>
                    </div>

                    <figcaption className='mt-4 flex items-center justify-between gap-4 text-[0.68rem]'>
                        <span className='font-mono font-semibold uppercase tracking-[0.14em] text-brand'>
                            Videos on the big screen
                        </span>

                        <span className='text-muted-foreground'>
                            Illustrative view.
                        </span>
                    </figcaption>
                </figure>
            </div>
        </section>
    )
}

export default GoogleTvSection
